import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiExternalLink, FiSend, FiCheck } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }, 3000);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Address',
      value: '81-83 Campbell St, Surry Hills, NSW 2010',
      link: 'https://maps.google.com/?q=81-83+Campbell+St,+Surry+Hills,+NSW+2010'
    }
  ];

  return (
    <section id="contact" className="py-12 md:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            Get In <span className="text-primary-600">Touch</span>
          </h2>
          <div className="w-16 md:w-20 h-1 bg-primary-600 mx-auto mb-4 md:mb-6"></div>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to automate your business processes? Let's discuss how we can help you achieve more in less time.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl p-6 md:p-8"
          >
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="+61 xxx xxx xxx"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Tell us about your automation needs..."
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || isSubmitted}
                className={`w-full py-3 md:py-4 px-4 md:px-6 rounded-lg font-semibold text-base md:text-lg transition-all duration-300 flex items-center justify-center space-x-2 ${
                  isSubmitted
                    ? 'bg-green-500 text-white'
                    : isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg transform hover:-translate-y-1'
                }`}
              >
                {isSubmitted ? (
                  <>
                    <SafeIcon icon={FiCheck} className="h-4 w-4 md:h-5 md:w-5" />
                    <span>Message Sent!</span>
                  </>
                ) : isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="h-4 w-4 md:h-5 md:w-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6 md:space-y-8"
          >
            {/* Contact Cards */}
            <div className="space-y-4 md:space-y-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="bg-white rounded-xl md:rounded-2xl shadow-md md:shadow-lg p-5 md:p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="bg-primary-100 p-2 md:p-3 rounded-lg">
                      <SafeIcon icon={info.icon} className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">{info.title}</h4>
                      <a
                        href={info.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm md:text-base text-gray-600 hover:text-primary-600 transition-colors flex items-center space-x-1 md:space-x-2"
                      >
                        <span>{info.value}</span>
                        <SafeIcon icon={FiExternalLink} className="h-3 w-3 md:h-4 md:w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl md:rounded-2xl p-6 md:p-8 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Ready to Get Started?</h3>
              <p className="text-sm md:text-base text-primary-100 mb-5 md:mb-6 leading-relaxed">
                Book a free discovery call to discuss your automation needs and see how we can help transform your business.
              </p>
              <a
                href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white text-primary-600 px-5 py-2 md:px-6 md:py-3 rounded-lg hover:bg-gray-100 transition-all duration-300 font-semibold text-sm md:text-base"
              >
                <span>Book Discovery Call</span>
                <SafeIcon icon={FiExternalLink} className="h-3 w-3 md:h-4 md:w-4" />
              </a>
            </div>

            {/* Map Placeholder */}
            <div className="bg-white rounded-xl md:rounded-2xl shadow-md md:shadow-lg overflow-hidden">
              <div className="h-48 md:h-64 bg-gray-200 flex items-center justify-center">
                <div className="text-center">
                  <SafeIcon icon={FiMapPin} className="h-10 w-10 md:h-12 md:w-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                  <p className="text-sm md:text-base text-gray-600 font-medium">Surry Hills, NSW</p>
                  <a
                    href="https://maps.google.com/?q=81-83+Campbell+St,+Surry+Hills,+NSW+2010"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm md:text-base text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center space-x-1 mt-1 md:mt-2"
                  >
                    <span>View on Google Maps</span>
                    <SafeIcon icon={FiExternalLink} className="h-3 w-3 md:h-4 md:w-4" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;